import React, { useEffect, useState, useCallback, useRef } from "react";
import Home from "@/components/steps/Home";
import Consent from "@/components/steps/Consent";
import Prep from "@/components/steps/Prep";
import Capture from "@/components/steps/Capture";
import Processing from "@/components/steps/Processing";
import Questionnaire from "@/components/steps/Questionnaire";
import Report from "@/components/steps/Report";
import PrivacyPage from "@/pages/PrivacyPage";
import { createSession, runQualityGate, runAnalyze, submitQuestionnaire, getConfig } from "@/lib/api";

const VIEWS = ["front", "left", "right"];
const PROC_STEPS = [
  "Controlliamo la qualità delle immagini…",
  "Analizziamo le caratteristiche visibili…",
  "Prepariamo il tuo Skin Profile…",
];

export default function SkinFlow() {
  const [step, setStep] = useState("home");
  const [prevStep, setPrevStep] = useState("home");
  const [settings, setSettings] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [images, setImages] = useState({});
  const [retakeViews, setRetakeViews] = useState(null);
  const [retakeMessages, setRetakeMessages] = useState({});
  const [procActive, setProcActive] = useState(0);
  const [procError, setProcError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [report, setReport] = useState(null);
  const runningRef = useRef(false);

  useEffect(() => {
    getConfig().then((c) => setSettings(c.settings)).catch(() => {});
  }, []);

  const goPrivacy = () => {
    setPrevStep(step);
    setStep("privacy");
  };

  const restart = () => {
    setImages({});
    setAnalysis(null);
    setReport(null);
    setRetakeViews(null);
    setRetakeMessages({});
    setSessionId(null);
    setProcError(null);
    setStep("home");
  };

  const start = async () => {
    try {
      const s = await createSession();
      setSessionId(s.session_id);
      setStep("consent");
    } catch {
      setSessionId(null);
      setStep("consent");
    }
  };

  const onCaptureDone = (captured) => {
    const merged = { ...images, ...captured };
    setImages(merged);
    setRetakeViews(null);
    setRetakeMessages({});
    setProcError(null);
    setProcActive(0);
    setStep("processing");
  };

  const runPipeline = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setProcError(null);
    try {
      setProcActive(0);
      const q = await runQualityGate(sessionId, images);
      if (!q.all_pass) {
        const failed = VIEWS.filter((v) => q.views[v]?.status === "retake");
        const msgs = {};
        failed.forEach((v) => (msgs[v] = q.views[v]?.messages || []));
        setRetakeViews(failed);
        setRetakeMessages(msgs);
        runningRef.current = false;
        setStep("capture");
        return;
      }
      setProcActive(1);
      const a = await runAnalyze(sessionId, images);
      setAnalysis(a);
      setProcActive(2);
      // brief pause so the final calm message is visible
      await new Promise((r) => setTimeout(r, 600));
      runningRef.current = false;
      setStep("questionnaire");
    } catch (e) {
      runningRef.current = false;
      const detail = e?.response?.data?.detail;
      setProcError(typeof detail === "string" ? detail : "Non è stato possibile completare l'analisi. Riprova.");
    }
  }, [sessionId, images]);

  useEffect(() => {
    if (step === "processing" && !procError) runPipeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const onQuestionnaireDone = async (answers) => {
    setProcError(null);
    setProcActive(2);
    setStep("finalizing");
    try {
      const r = await submitQuestionnaire(sessionId, answers);
      setReport(r);
      setStep("report");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setProcError(typeof detail === "string" ? detail : "Non è stato possibile preparare il profilo. Riprova.");
    }
  };

  if (step === "privacy") return <PrivacyPage settings={settings} onBack={() => setStep(prevStep)} />;
  if (step === "home") return <Home onStart={start} onPrivacy={goPrivacy} />;
  if (step === "consent") return <Consent onAccept={() => setStep("prep")} onBack={() => setStep("home")} onPrivacy={goPrivacy} />;
  if (step === "prep") return <Prep onReady={() => setStep("capture")} onBack={() => setStep("consent")} />;
  if (step === "capture")
    return (
      <Capture
        views={retakeViews || VIEWS}
        messages={retakeMessages}
        existing={images}
        onDone={onCaptureDone}
        onBack={() => setStep("prep")}
      />
    );
  if (step === "processing")
    return (
      <Processing
        steps={PROC_STEPS}
        active={procActive}
        error={procError}
        onRetry={() => {
          setProcError(null);
          runPipeline();
        }}
        onRestart={restart}
      />
    );
  if (step === "questionnaire") return <Questionnaire onDone={onQuestionnaireDone} onBack={() => setStep("prep")} />;
  if (step === "finalizing")
    return <Processing steps={PROC_STEPS} active={2} error={procError} onRetry={null} onRestart={restart} />;
  if (step === "report" && report && settings)
    return <Report report={report} settings={settings} sessionId={sessionId} onRestart={restart} />;

  return <Processing steps={PROC_STEPS} active={0} error={null} onRestart={restart} />;
}
